<template>
  <div
    class="phase-explorer"
    :class="{ collapsed, peeking: collapsed && peeking }"
    :style="(!collapsed || peeking) ? { width: width + 'px' } : null"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @click="onPanelClick"
  >
    <div v-if="!collapsed" class="pe-resize" title="Drag to resize" @mousedown="startResize"></div>
    <div class="pe-head">
      <span v-if="!collapsed || peeking" class="pe-title">{{ selectedPhase ? 'Phase Details' : 'Client View' }}</span>
      <button class="pe-toggle" :title="collapsed ? 'Expand' : 'Collapse'" @click="collapsed = !collapsed">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
    </div>

    <div v-if="!collapsed || peeking" class="pe-body">
      <div v-if="loading" class="pe-loading">Generating client presentation…</div>

      <template v-else-if="presentation">
        <!-- Detail view for the currently selected phase -->
        <div v-if="selectedPhase">
          <button class="pe-back" @click="$emit('deselect')">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            Back to phases
          </button>
          <span class="pe-badge" :style="chipStyle(selectedPhase.color)">{{ typeLabel(selectedPhase.type) }}</span>
          <div class="pe-detail-name">{{ selectedPhase.label }}</div>
          <div class="pe-detail-desc">{{ selectedPhase.description }}</div>

          <div class="pe-detail-section">
            <div class="pe-detail-h">Stages ({{ selectedPhase.stages.length }})</div>
            <div class="pe-stage-chips">
              <span v-for="s in selectedPhase.stages" :key="s" class="pe-stage-chip">{{ s }}</span>
            </div>
          </div>

          <div v-if="selectedPhase.key_questions && selectedPhase.key_questions.length" class="pe-detail-section">
            <div class="pe-detail-h">Key Questions</div>
            <div v-for="(q, qi) in selectedPhase.key_questions" :key="qi" class="pe-q-card">
              <div class="pe-q-stage">{{ q.stage }}</div>
              <div class="pe-q-text">{{ q.question_summary }}</div>
              <div class="pe-q-opts">
                <div v-for="(o, oi) in q.options_summary" :key="oi" class="pe-q-opt">{{ o }}</div>
              </div>
            </div>
          </div>

          <div v-if="selectedPhase.branches_to && selectedPhase.branches_to.length" class="pe-detail-section">
            <div class="pe-detail-h">Branches To</div>
            <div v-for="(b, bi) in selectedPhase.branches_to" :key="bi" class="pe-branch-item" @click="$emit('select-phase', b.target_phase)">
              <span class="pe-phase-chip" :style="chipStyleByPhaseId(b.target_phase)">{{ phaseLabel(b.target_phase) }}</span>
              <span class="pe-branch-cond">{{ b.condition }}</span>
            </div>
          </div>

          <div v-if="selectedPhase.exit_points && selectedPhase.exit_points.length" class="pe-detail-section">
            <div class="pe-detail-h">Exit Points</div>
            <div v-for="(ep, ei) in selectedPhase.exit_points" :key="ei" class="pe-exit-item">
              <span class="pe-mono">{{ ep.stage }}</span> — {{ ep.reason }}
            </div>
          </div>
        </div>

        <!-- Phase list -->
        <div v-else>
          <div v-if="presentation.summary" class="pe-summary">{{ presentation.summary }}</div>

          <div class="pe-stages-head">Phases · {{ phases.length }}</div>
          <div class="pe-list">
            <div v-for="p in phases" :key="p.id" class="pe-card" @click="$emit('select-phase', p.id)">
              <div class="pe-name">{{ p.label }}</div>
              <div class="pe-meta">
                <span class="pe-badge" :style="chipStyle(p.color)">{{ typeLabel(p.type) }}</span>
                <span class="pe-desc">{{ p.stages.length }} stage{{ p.stages.length === 1 ? '' : 's' }}</span>
              </div>
            </div>
          </div>

          <div v-if="presentation.loops && presentation.loops.length" class="pe-detail-section">
            <div class="pe-detail-h">Loops</div>
            <div v-for="(loop, li) in presentation.loops" :key="li" class="pe-loop-item">
              <span class="pe-phase-chip" :style="chipStyleByPhaseId(loop.from_phase)">{{ phaseLabel(loop.from_phase) }}</span>
              <span class="pe-loop-arrow">↻</span>
              <span class="pe-phase-chip" :style="chipStyleByPhaseId(loop.to_phase)">{{ phaseLabel(loop.to_phase) }}</span>
              <div class="pe-loop-trigger">{{ loop.trigger }}</div>
            </div>
          </div>

          <div v-if="presentation.exit_points_summary && presentation.exit_points_summary.length" class="pe-detail-section">
            <div class="pe-detail-h">Exit Points</div>
            <div v-for="(ep, ei) in presentation.exit_points_summary" :key="ei" class="pe-exit-item">
              <span class="pe-mono">{{ ep.stage }}</span> — {{ ep.reason }}
            </div>
          </div>
        </div>
      </template>

      <div v-else class="pe-empty">No presentation generated yet.</div>
    </div>
  </div>
</template>

<script>
const PALETTE = {
  pink: { c: '#E91E8C', light: '#FCE4EC' },
  blue: { c: '#1565C0', light: '#E3F2FD' },
  green: { c: '#2E7D32', light: '#E8F5E9' },
  orange: { c: '#F57C00', light: '#FFF3E0' },
  purple: { c: '#6A1B9A', light: '#F3E5F5' },
  teal: { c: '#00897B', light: '#E0F2F1' },
  slate: { c: '#37474F', light: '#ECEFF1' },
  amber: { c: '#F9A825', light: '#FFF8E1' }
}
const TYPE_LABELS = {
  gate: 'Entry / Gate',
  onboarding: 'Onboarding',
  education: 'Education',
  intent: 'Intent',
  barrier: 'Barrier',
  screening: 'Screening',
  referral: 'Referral',
  conversion: 'Conversion',
  exit: 'Exit'
}

export default {
  name: 'phase-explorer',
  props: {
    presentation: { type: Object, default: null },
    selectedPhase: { type: Object, default: null },
    loading: { type: Boolean, default: false }
  },
  data () {
    return {
      collapsed: true,
      autoOpened: false,
      justOpened: false,
      peeking: false,
      resizing: false,
      width: 320
    }
  },
  computed: {
    phases () {
      return (this.presentation && this.presentation.phases) || []
    },
    isOpen () {
      return !this.collapsed || this.peeking
    },
    effectiveWidth () {
      return this.isOpen ? this.width : 40
    }
  },
  watch: {
    selectedPhase (val) {
      if (val) {
        // See StageExplorer's identical guard: selecting a phase from outside this
        // panel can auto-open it within the same click whose document listener then
        // runs the outside-click-collapse check — justOpened skips that one check.
        if (this.collapsed) { this.collapsed = false; this.autoOpened = true; this.justOpened = true }
      } else if (this.autoOpened) {
        this.collapsed = true
        this.autoOpened = false
      }
    },
    loading (val) {
      if (val && this.collapsed) this.collapsed = false
    },
    effectiveWidth (val) {
      this.$emit('width-change', val)
    }
  },
  mounted () {
    this.onDocClick = e => {
      if (this.justOpened) { this.justOpened = false; return }
      if (!this.resizing && !this.collapsed && !this.$el.contains(e.target)) this.collapsed = true
    }
    document.addEventListener('click', this.onDocClick)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    typeLabel (type) {
      return TYPE_LABELS[type] || type
    },
    phaseLabel (id) {
      const p = this.phases.find(ph => ph.id === id)
      return p ? p.label : id
    },
    chipStyle (color) {
      const pal = PALETTE[color] || PALETTE.slate
      return { background: pal.light, color: pal.c, borderColor: pal.c }
    },
    chipStyleByPhaseId (id) {
      const p = this.phases.find(ph => ph.id === id)
      return this.chipStyle(p ? p.color : 'slate')
    },
    onMouseEnter () {
      if (this.collapsed) this.peeking = true
    },
    onMouseLeave () {
      this.peeking = false
    },
    onPanelClick () {
      if (this.collapsed && this.peeking) {
        this.collapsed = false
        this.peeking = false
      }
    },
    setCollapsed (v) {
      this.collapsed = v
    },
    startResize (e) {
      const startX = e.clientX
      const startW = this.width
      this.resizing = true
      const onMove = (ev) => {
        let newW = startW - (ev.clientX - startX)
        newW = Math.max(260, Math.min(480, newW))
        this.width = newW
      }
      const onUp = () => {
        this.resizing = false
        document.body.classList.remove('resizing-phase-explorer')
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      document.body.classList.add('resizing-phase-explorer')
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
      e.preventDefault()
    }
  }
}
</script>

<style>
body.resizing-phase-explorer{cursor:col-resize;user-select:none}
</style>

<style scoped>
.phase-explorer{
  --card:#ffffff;--card-border:#E2E8EE;--card-label-bg:#F6F8FA;
  --text:#16233A;--text-muted:#5D6C7E;--text-faint:#94A2B1;
  --accent:#0D9488;--accent-light:#0B7A70;--accent-strong:#0B7A70;
  --btn-hl-border:rgba(13,148,136,0.4);
  position:fixed;top:0;right:0;bottom:0;
  background:var(--card);border-left:1px solid var(--card-border);
  box-shadow:-4px 0 16px rgba(0,0,0,0.06);
  display:flex;flex-direction:column;overflow:hidden;z-index:900;
  transition:width .15s ease,box-shadow .15s ease;
}
.phase-explorer.collapsed{width:40px}
.phase-explorer.peeking{box-shadow:-8px 0 28px rgba(0,0,0,0.16)}
.pe-resize{position:absolute;top:0;left:0;width:6px;height:100%;cursor:col-resize;z-index:2}
.pe-head{
  padding:10px 12px;border-bottom:1px solid var(--card-border);
  display:flex;align-items:center;gap:8px;flex-shrink:0;
}
.phase-explorer.collapsed:not(.peeking) .pe-head{justify-content:center;padding:10px 4px}
.pe-title{font-size:11px;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.05em;flex:1}
.pe-toggle{
  width:24px;height:24px;border-radius:6px;background:transparent;border:1px solid transparent;
  color:var(--text-muted);cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;
}
.pe-toggle:hover{background:var(--card-label-bg);color:var(--text);border-color:var(--card-border)}
.pe-toggle svg{transition:transform .15s}
.phase-explorer.collapsed .pe-toggle svg{transform:rotate(180deg)}

.pe-body{flex:1;overflow-y:auto;padding:14px;display:flex;flex-direction:column}
.pe-body::-webkit-scrollbar{width:6px}
.pe-body::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:3px}
.pe-loading,.pe-empty{font-size:12px;color:var(--text-muted);padding-top:8px}

.pe-summary{
  font-size:12px;line-height:1.6;color:var(--text-muted);
  background:var(--card-label-bg);border:1px solid var(--card-border);
  border-radius:8px;padding:10px 12px;margin-bottom:14px;
}
.pe-stages-head{font-size:10.5px;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px}

.pe-list{display:flex;flex-direction:column;gap:5px}
.pe-card{
  padding:9px 10px;border:1px solid var(--card-border);border-radius:8px;
  background:var(--card);cursor:pointer;transition:all .1s;
}
.pe-card:hover{border-color:var(--btn-hl-border);background:var(--card-label-bg)}
.pe-name{font-size:12.5px;font-weight:600;color:var(--text)}
.pe-meta{font-size:10.5px;color:var(--text-muted);margin-top:5px;display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.pe-badge{display:inline-flex;align-items:center;font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.03em;padding:2px 8px;border-radius:999px;border:1px solid}

.pe-back{
  display:inline-flex;align-items:center;gap:5px;background:none;border:none;
  color:var(--accent-light);cursor:pointer;font-size:12px;font-weight:500;padding:0;margin-bottom:12px;
}
.pe-back:hover{text-decoration:underline}
.pe-detail-name{font-size:14px;font-weight:700;color:var(--text);margin-top:8px;margin-bottom:4px;line-height:1.3}
.pe-detail-desc{font-size:12px;color:var(--text-muted);line-height:1.55}
.pe-detail-section{margin-top:16px}
.pe-detail-h{font-size:10.5px;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px}

.pe-stage-chips{display:flex;flex-wrap:wrap;gap:6px}
.pe-stage-chip{
  font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--text-muted);
  background:var(--card-label-bg);border:1px solid var(--card-border);border-radius:5px;padding:2px 7px;
}
.pe-q-card{background:var(--card-label-bg);border:1px solid var(--card-border);border-radius:8px;padding:10px 11px;margin-bottom:8px}
.pe-q-stage{font-family:'JetBrains Mono',monospace;font-size:9.5px;color:var(--text-faint);margin-bottom:4px}
.pe-q-text{font-size:12px;font-weight:600;color:var(--text);line-height:1.4}
.pe-q-opts{margin-top:6px;display:flex;flex-direction:column;gap:3px}
.pe-q-opt{font-size:11px;color:var(--text-muted);padding-left:12px;position:relative}
.pe-q-opt::before{content:'•';position:absolute;left:1px;color:var(--text-faint)}

.pe-branch-item{display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--card-border);cursor:pointer}
.pe-branch-item:hover .pe-branch-cond{color:var(--text)}
.pe-branch-item:last-child{border-bottom:none}
.pe-branch-cond{font-size:11.5px;color:var(--text-muted)}
.pe-phase-chip{display:inline-block;font-size:10.5px;font-weight:600;border-radius:999px;padding:1px 8px;border:1px solid;white-space:nowrap;flex-shrink:0}

.pe-loop-item{background:var(--card-label-bg);border:1px solid var(--card-border);border-radius:8px;padding:9px 10px;margin-bottom:7px;font-size:11.5px}
.pe-loop-arrow{margin:0 5px;color:var(--text-faint)}
.pe-loop-trigger{margin-top:5px;font-size:10.5px;color:var(--text-muted)}
.pe-exit-item{font-size:11.5px;color:var(--text-muted);padding:5px 0;border-bottom:1px solid var(--card-border)}
.pe-exit-item:last-child{border-bottom:none}
.pe-mono{font-family:'JetBrains Mono',monospace;font-size:10.5px;color:var(--text)}
</style>



// WEBPACK FOOTER //
// PhaseExplorer.vue?6925cc36